import { useState } from "react";
import { Calendar, Mail, Loader2, Lock, RefreshCw, MoreHorizontal, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  getGoogleConnections,
  startGoogleConnect,
  completeGoogleConnect,
  disconnectGoogleConnector,
} from "@/lib/google-connect.functions";
import {
  syncGoogleCalendar,
  getCalendarStatus,
  diagnoseCalendarSync,
} from "@/lib/calendar.functions";
import { runGoogleConnect, type GoogleConnectorId } from "@/lib/google-connect-popup";
import { CalendarPicker } from "@/components/calendar-picker";
import { syncMyEmail, getEmailSyncState } from "@/lib/email-sync.functions";
import { openUpgrade } from "@/lib/upgrade-store";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet } from "@/components/ui/sheet";
import { ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function useGoogleConnections() {
  const fetchConnections = useServerFn(getGoogleConnections);
  return useQuery({
    queryKey: ["google-connections"],
    queryFn: () => fetchConnections(),
    staleTime: 30_000,
  });
}

function relative(iso: string | null | undefined) {
  if (!iso) return null;
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const META: Record<GoogleConnectorId, { label: string; icon: typeof Calendar }> = {
  google_calendar: { label: "Google Calendar", icon: Calendar },
  google_mail: { label: "Gmail", icon: Mail },
};

export function GoogleConnectionRow({
  connectorId,
  locked,
}: {
  connectorId: GoogleConnectorId;
  locked?: boolean;
}) {
  const qc = useQueryClient();
  const { data: connections } = useGoogleConnections();
  const start = useServerFn(startGoogleConnect);
  const complete = useServerFn(completeGoogleConnect);
  const disconnectFn = useServerFn(disconnectGoogleConnector);
  const syncCal = useServerFn(syncGoogleCalendar);
  const fetchCalStatus = useServerFn(getCalendarStatus);
  const diagnose = useServerFn(diagnoseCalendarSync);
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [diag, setDiag] = useState<any | null>(null);
  const [showCalendars, setShowCalendars] = useState(false);

  const state = connections?.[connectorId];
  const connected = !!state?.connected;
  const available = state?.available !== false;

  const { data: calStatus } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: () => fetchCalStatus().catch(() => null),
    retry: false,
    enabled: connectorId === "google_calendar" && connected,
  });

  const fetchMailState = useServerFn(getEmailSyncState);
  const syncMail = useServerFn(syncMyEmail);
  const { data: mailStatus } = useQuery({
    queryKey: ["email-sync-state"],
    queryFn: () => fetchMailState().catch(() => null),
    retry: false,
    enabled: connectorId === "google_mail" && connected,
  });

  // Reads message headers only — never bodies — so Pyng knows when you last
  // spoke and whether they wrote back.
  const mailSyncMut = useMutation({
    mutationFn: () => syncMail({ data: {} }),
    onSuccess: (r: any) => {
      const logged = r?.logged ?? 0;
      if (logged === 0) {
        toast.success("Up to date — no new email with your people");
      } else {
        const parts = [`${logged} touch${logged === 1 ? "" : "es"} confirmed`];
        if (r?.inbound > 0) parts.push(`${r.inbound} reply${r.inbound === 1 ? "" : "s"}`);
        toast.success(parts.join(" · "));
      }
      qc.invalidateQueries({ queryKey: ["email-sync-state"] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["people"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Email sync failed"),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["google-connections"] });
    qc.invalidateQueries({ queryKey: ["calendar-status"] });
  };

  const connect = async () => {
    setBusy(true);
    try {
      await runGoogleConnect(connectorId, (args) => start(args), (args) => complete(args));
      toast.success(`${META[connectorId].label} connected`);
      refresh();
      // Right after connecting is when "don't read my Holidays calendar" matters,
      // so the picker opens itself instead of hiding behind another tap.
      if (connectorId === "google_calendar") setShowCalendars(true);
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't connect");
    } finally {
      setBusy(false);
    }
  };

  // Switch account: drop the old connection, let Google show its account
  // chooser, then resync immediately so Today reflects the new account
  // without the user guessing which one Pyng is reading.
  const switchAccount = async () => {
    setSwitching(true);
    setDiag(null);
    try {
      await runGoogleConnect(
        connectorId,
        (args) => start(args),
        (args) => complete(args),
        { switchAccount: true },
      );
      refresh();
      if (connectorId === "google_calendar") {
        toast.success("Account switched — syncing calendar…");
        qc.invalidateQueries({ queryKey: ["google-calendar-list"] });
        setShowCalendars(true);
        await syncMut.mutateAsync().catch(() => {});
        const status: any = await fetchCalStatus().catch(() => null);
        if (status?.accountEmail) toast.success(`Now reading ${status.accountEmail}`);
      } else {
        toast.success("Account switched");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't switch account", {
        description: "Your previous connection was removed — tap Connect to reconnect.",
      });
      refresh();
    } finally {
      setSwitching(false);
    }
  };

  const disconnectMut = useMutation({
    mutationFn: () => disconnectFn({ data: { connectorId } }),
    onSuccess: () => {
      toast.success(`${META[connectorId].label} disconnected`);
      refresh();
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't disconnect"),
  });

  const syncMut = useMutation({
    mutationFn: () => syncCal({ data: {} }),
    onSuccess: (r: any) => {
      // "Synced 5 events" counted refreshed and long-past meetings alike, so a
      // sync that changed nothing looked like it found five new ones. Report
      // what actually changed, and whether any of it can reach Today.
      const added = r?.eventsInserted ?? 0;
      const updated = r?.eventsUpdated ?? 0;
      const removed = r?.eventsRemoved ?? 0;
      const upcoming = r?.upcomingInserted ?? 0;
      if (added === 0 && removed === 0) {
        toast.success("Up to date — no new meetings", {
          description: "Not seeing one you expected? Tap Missing a meeting?",
          action: { label: "Check", onClick: () => diagMut.mutate() },
        });
      } else {
        const parts: string[] = [];
        if (added > 0) parts.push(`${added} new`);
        if (updated > 0) parts.push(`${updated} updated`);
        if (removed > 0) parts.push(`${removed} removed`);
        toast.success(parts.join(" · "), {
          description:
            added > 0 && upcoming === 0
              ? "Nothing new in the next 7 days — the new events are in the past."
              : undefined,
        });
      }
      qc.invalidateQueries({ queryKey: ["calendar-status"] });
      qc.invalidateQueries({ queryKey: ["pending-hooks"] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      // These are the keys Today actually reads — the old "upcoming-events"
      // key belongs to no screen, so a new meeting never appeared until reload.
      qc.invalidateQueries({ queryKey: ["upcoming-meetings"] });
      qc.invalidateQueries({ queryKey: ["post-meeting-recaps"] });
      qc.invalidateQueries({ queryKey: ["upcoming-events"] });
      qc.invalidateQueries({ queryKey: ["sync-history"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Calendar sync failed"),
  });

  // "Why isn't my meeting here?" — shows exactly which Google account and
  // calendars Pyng can read, so a second-account mixup is obvious.
  const diagMut = useMutation({
    mutationFn: () => diagnose(),
    onSuccess: (r: any) => setDiag(r),
    onError: (e: any) => toast.error(e?.message ?? "Couldn't read your calendars"),
  });

  const Icon = META[connectorId].icon;
  const synced = connectorId === "google_calendar" ? relative(calStatus?.lastSyncedAt) : null;
  const syncError = connectorId === "google_calendar" ? (calStatus as any)?.lastSyncError : null;
  const isMobile = useIsMobile();
  const isCal = connectorId === "google_calendar";
  const isMail = connectorId === "google_mail";
  const mailSynced = isMail ? relative((mailStatus as any)?.lastSyncedAt) : null;
  // A grant that died about a week after consent wasn't revoked by anyone —
  // Google timed it out. Saying so stops the reconnect-every-week guessing.
  const breakKind = (state as any)?.breakKind ?? "none";
  const broken = breakKind !== "none";
  const brokenLabel = breakKind === "expired" ? "Access expired — reconnect" : "Needs reconnect";
  const connectedAge = relative((state as any)?.connectedAt);
  const lastGood = synced ?? mailSynced ?? relative((state as any)?.lastSyncedAt);
  const statusDetail = broken
    ? [connectedAge ? `connected ${connectedAge}` : null, lastGood ? `last sync ${lastGood}` : null]
        .filter(Boolean)
        .join(" · ")
    : lastGood
      ? `· ${isMail ? "read" : "auto-synced"} ${lastGood}`
      : isCal
        ? "· auto-syncs every 30 min"
        : "· checks every 2 hrs";


  const diagBody = diag ? (
    <div className="space-y-2 text-[13px] text-muted-foreground">
      <p className="text-foreground/80">
        Reading {diag.calendars?.find((c: any) => c.primary)?.calendarId ?? "your Google account"}
      </p>
      <ul className="divide-y divide-foreground/10">
        {(diag.calendars ?? []).map((c: any) => {
          const live = (c.items ?? []).filter((i: any) => i.status !== "cancelled").length;
          return (
            <li key={c.calendarId} className="flex items-start gap-2 py-2">
              <span className="min-w-0 flex-1 truncate text-foreground/90">{c.name}</span>
              <span className="shrink-0 text-right text-[12px]">
                {c.error
                  ? `couldn't read`
                  : !c.syncedByUs
                    ? "skipped"
                    : `${live} event${live === 1 ? "" : "s"} / 2 wks`}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-[12px]">
        A meeting missing from this list lives on a different Google account — connect that account
        instead (Switch account), or invite this one.
      </p>
    </div>
  ) : null;

  const calendarsBody = <CalendarPicker />;

  const overflow = connected && !locked && available && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More options"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground sm:h-9 sm:w-9"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          className="h-11 gap-2 sm:h-9"
          disabled={switching}
          onSelect={(e) => {
            e.preventDefault();
            if (!switching) switchAccount();
          }}
        >
          {switching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          )}
          {switching ? "Switching…" : "Switch account"}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="h-11 gap-2 text-destructive focus:text-destructive sm:h-9"
          disabled={disconnectMut.isPending}
          onSelect={(e) => {
            e.preventDefault();
            if (!disconnectMut.isPending) disconnectMut.mutate();
          }}
        >
          <X className="h-4 w-4" /> Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex w-full flex-col gap-2 px-4 py-3 text-left text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          {META[connectorId].label}
          {isCal && (calStatus as any)?.accountEmail && (
            <span className="ml-1.5 block truncate text-[12px] text-muted-foreground sm:inline sm:ml-2">
              {(calStatus as any).accountEmail}
            </span>
          )}
        </span>
        <div className="sm:hidden">{overflow}</div>
      </div>

      {connected && !locked && available && (
        <span className="inline-flex flex-wrap items-center gap-1.5 pl-7 text-[13px] sm:hidden">
          {broken ? (
            <span className="text-amber-500">
              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
              {brokenLabel}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Connected
            </span>
          )}
          <span className="text-muted-foreground">{statusDetail}</span>
        </span>
      )}


      <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-2 text-[14px] text-muted-foreground sm:ml-auto sm:pl-0">
        {!available ? (
          <span className="pl-7 text-muted-foreground sm:pl-0">Setup needed</span>
        ) : locked ? (
          <button
            type="button"
            onClick={() => openUpgrade("live-context")}
            className="ml-7 inline-flex h-11 items-center gap-1.5 rounded-full glass-soft px-4 text-[13px] font-medium text-foreground sm:ml-0 sm:h-9 sm:px-3"
          >
            <Lock className="h-3 w-3 text-[var(--accent-neon-ink)]" strokeWidth={1.75} /> Upgrade
          </button>
        ) : connected ? (
          <>
            <span className="mr-auto hidden items-center gap-1.5 sm:inline-flex sm:mr-0">
              <span
                className={
                  "h-1.5 w-1.5 rounded-full " + (broken ? "bg-amber-500" : "bg-emerald-400")
                }
              />
              <span className={broken ? "text-amber-500" : "text-emerald-400"}>
                {broken ? brokenLabel : "Connected"}
              </span>
              <span className="text-muted-foreground">{statusDetail}</span>
            </span>

            {isMail && (
              <button
                type="button"
                onClick={() => !mailSyncMut.isPending && mailSyncMut.mutate()}
                disabled={mailSyncMut.isPending}
                className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-[14px] font-medium text-primary-foreground disabled:opacity-50 sm:h-9 sm:w-auto sm:bg-transparent sm:px-3 sm:text-[13px] sm:text-foreground sm:glass-soft"
              >
                {mailSyncMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {mailSyncMut.isPending ? "Reading…" : "Sync now"}
              </button>
            )}
            {isCal && (
              <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
                <button
                  type="button"
                  onClick={() => !syncMut.isPending && syncMut.mutate()}
                  disabled={syncMut.isPending}
                  className="col-span-2 inline-flex h-11 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-[14px] font-medium text-primary-foreground disabled:opacity-50 sm:col-span-1 sm:h-9 sm:bg-transparent sm:px-3 sm:text-[13px] sm:text-foreground sm:glass-soft"
                >
                  {syncMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {syncMut.isPending ? "Syncing…" : "Sync now"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCalendars((v) => !v)}
                  className="inline-flex h-11 items-center justify-center rounded-full glass-soft px-3 text-[13px] font-medium text-foreground/80 sm:h-9 sm:rounded-none sm:bg-transparent sm:px-2 sm:text-muted-foreground sm:backdrop-blur-none sm:hover:text-foreground"
                >
                  {showCalendars && !isMobile ? "Hide calendars" : "Calendars"}
                </button>
                <button
                  type="button"
                  onClick={() => (diag ? setDiag(null) : !diagMut.isPending && diagMut.mutate())}
                  disabled={diagMut.isPending}
                  className="inline-flex h-11 items-center justify-center rounded-full glass-soft px-3 text-[13px] font-medium text-foreground/80 disabled:opacity-50 sm:h-9 sm:rounded-none sm:bg-transparent sm:px-2 sm:text-muted-foreground sm:backdrop-blur-none sm:hover:text-foreground"
                >
                  {diagMut.isPending ? "Checking…" : diag && !isMobile ? "Hide check" : "Missing a meeting?"}
                </button>
              </div>
            )}
            <div className="hidden sm:block">{overflow}</div>
          </>
        ) : (
          <>
            <span className="mr-auto pl-7 text-muted-foreground sm:hidden sm:pl-0">Not connected</span>
            <button
            type="button"
            onClick={() => !busy && connect()}
            disabled={busy}
            className="inline-flex h-11 items-center gap-1 rounded-full bg-primary px-4 text-[14px] font-medium text-primary-foreground disabled:opacity-50 sm:h-9 sm:px-3 sm:text-[13px]"
          >
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            {busy ? "Connecting…" : "Connect"}
            </button>
          </>
        )}
      </div>
      {syncError && (
        <p className="pl-7 text-[12px] text-amber-400 sm:pl-0 sm:basis-full">
          Last sync failed: {String(syncError).slice(0, 140)}
        </p>
      )}

      {/* Mobile: calendars + diagnostics live in bottom sheets so they read as
          their own panel instead of indented text under the row. */}
      {isCal && connected && !isMobile && showCalendars && (
        <div className="pl-7 sm:basis-full sm:pl-0">{calendarsBody}</div>
      )}
      {!isMobile && diag && (
        <div className="pl-7 sm:basis-full sm:pl-0">{diagBody}</div>
      )}
      {isMobile && (
        <>
          <Sheet open={isCal && connected && showCalendars} onOpenChange={(v) => !v && setShowCalendars(false)}>
            <ResponsiveModalContent className="max-h-[85vh] overflow-y-auto rounded-t-3xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <SheetHeader className="text-left">
                <SheetTitle>Calendars Pyng reads</SheetTitle>
                <SheetDescription>
                  Turning one off removes its meetings right away.
                </SheetDescription>
              </SheetHeader>
              <div className="pt-2">{calendarsBody}</div>
            </ResponsiveModalContent>
          </Sheet>
          <Sheet open={!!diag} onOpenChange={(v) => !v && setDiag(null)}>
            <ResponsiveModalContent className="max-h-[85vh] overflow-y-auto rounded-t-3xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <SheetHeader className="text-left">
                <SheetTitle>Missing a meeting?</SheetTitle>
                <SheetDescription>What Google returns for this account right now.</SheetDescription>
              </SheetHeader>
              <div className="pt-2">{diagBody}</div>
            </ResponsiveModalContent>
          </Sheet>
        </>
      )}
    </div>
  );
}
